import {decodeHTML} from "../../../../../app";

/**
 * @ngdoc directive
 * @name graphdb.framework.core.directives.open-in-sparql-editor.directive:openInSparqlEditor
 * @restrict E
 *
 * @description
 * This directive provides a button that allows users to open the SPARQL editor with a pre-defined query.
 * Optionally, it can handle repository switching before opening the editor and executing the query.
 * The directive can also trigger the query execution automatically if specified.
 *
 * @scope
 *
 * @param {string} query The SPARQL query to be opened and optionally executed in the SPARQL editor.
 * @param {string} repositoryId The ID of the repository to be selected before opening the SPARQL editor.
 * @param {string} executeQuery Flag that determines whether the query should be executed upon opening the editor.
 *                              It accepts 'true' or 'false'. If 'true', the query will be automatically executed.
 * @param {string} addPrefixes Flag that determines whether known prefixes should be added to the query before opening
 *                              the editor.
 *
 * @example
 * <open-in-sparql-editor
 *     query="SELECT * WHERE {?s ?p ?o}"
 *     repository-id="myRepository"
 *     execute-query="true">
 * </open-in-sparql-editor>
 *
 * @requires $repositories
 * @requires $translate
 * @requires ModalService
 * @requires $window
 *
 * @param {string} query The SPARQL query to be executed in the new tab.
 */

angular
    .module('graphdb.framework.core.directives.open-in-sparql-editor', [])
    .directive('openInSparqlEditor', openInSparqlEditorDirective);

openInSparqlEditorDirective.$inject = ['$repositories', '$translate', 'ModalService', '$window', 'SparqlRestService'];

function openInSparqlEditorDirective($repositories, $translate, ModalService, $window, SparqlRestService) {
    return {
        // Note: the line-height of the element must match the line-height of the icon.
        // This defines a composite FontAwesome icon to match the style of the surrounding icons.
        template: `
            <style>
                .open-in-sparql-editor-btn {
                    line-height: 0.75;
                }
                .open-in-sparql-editor-btn .fa:nth-child(2) {
                    margin-left: -0.2em;
                    margin-right: -0.2em;
                    font-size: 0.9em;
                }
            </style>
            <button class="btn btn-link btn-sm open-in-sparql-editor-btn"
                   data-test="open-in-sparql-editor-btn"
                   gdb-tooltip="{{'ttyg.chat_panel.btn.open_in_sparql_editor.tooltip' | translate}}" ng-click="onGoToSparqlEditorView()" guide-selector="open-in-sparql-editor-btn">
                <i class="ri-braces-line"></i>
            </button>
        `,
        restrict: 'E',
        scope: {
            query: '@',
            repositoryId: '@',
            executeQuery: '@',
            addPrefixes: '@',
        },
        link: function($scope, element) {
            // =========================
            // Public variables
            // =========================

            $scope.tooltipText = 'ttyg.chat_panel.btn.open_in_sparql_editor.tooltip';
            // =========================
            // Private functions
            // =========================
            const execute = $scope.executeQuery === 'true';
            const autoAddPrefixes = $scope.addPrefixes === 'true';

            // =========================
            // Public functions
            // =========================

            /**
             * Opens the SPARQL editor in a new browser tab with the specified query and optional execution.
             */
            $scope.onGoToSparqlEditorView = () => {
                const activeRepositoryId = $repositories.getActiveRepository();
                if (!activeRepositoryId || activeRepositoryId !== $scope.repositoryId) {
                    // Open a confirmation modal before switching the repository
                    ModalService.openConfirmationModal(
                        {
                            title: $translate.instant('common.confirm'),
                            message: decodeHTML($translate.instant('ttyg.chat_panel.dialog.confirm_repository_change.body', {repositoryId: $scope.repositoryId})),
                            confirmButtonKey: 'ttyg.chat_panel.btn.proceed.label',
                        },
                        () => {
                            $repositories.setRepository($repositories.getRepository($scope.repositoryId));
                            prepareAndOpenQuery($scope.query);
                        },
                    );
                } else {
                    // No repository switch needed, just open the SPARQL editor
                    prepareAndOpenQuery($scope.query);
                }
            };
            // =========================
            // Private functions
            // =========================

            /**
             * Opens SPARQL editor view with passed query and handles repository switch if necessary.
             * @param {string} query
             */
            const prepareAndOpenQuery = (query) => {
                if (autoAddPrefixes) {
                    // Open the tab NOW while the user-gesture is still active
                    const newTab = $window.open('about:blank', '_blank');
                    SparqlRestService.addKnownPrefixes(JSON.stringify(query))
                        .then((response) => {
                            const queryWithPrefixes = response.data;
                            openInSparqlEditorInNewTab(queryWithPrefixes, newTab);
                        })
                        .catch(() => {
                            // If there's an error adding prefixes, open the editor with the original query
                            openInSparqlEditorInNewTab(query, newTab);
                        });
                } else {
                    openInSparqlEditorInNewTab(query);
                }
            };

            const openInSparqlEditorInNewTab = (query, existingTab) => {
                const url = `sparql?query=${encodeURIComponent(query)}&execute=${execute}`;
                if (existingTab) {
                    // navigate the already-opened blank tab to the SPARQL editor with the query
                    existingTab.location.href = url;
                } else {
                    $window.open(url, '_blank');
                }
            };
        },
    };
}
